import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

const TIMEOUT = 10000;

export const useOwnerCheck = () => {
  const { user } = useAuth();
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const lastIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const check = async () => {
      if (!user || user.id !== lastIdRef.current) {
        setIsOwner(false);
        setLoading(true);
        lastIdRef.current = user?.id ?? null;
      }
      if (!user) { setLoading(false); return; }

      timeoutId = setTimeout(() => {
        if (!cancelled) {
          console.warn("[useOwnerCheck] safety timeout");
          setLoading(false);
        }
      }, TIMEOUT);

      try {
        // Direct read; avoids depending on has_role(_, 'owner') enum literal in client RPC
        const { data, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id)
          .eq("role", "owner" as any)
          .maybeSingle();
        if (cancelled) return;
        if (error) {
          console.error("[useOwnerCheck] error", error);
          setIsOwner(false);
        } else {
          setIsOwner(!!data);
        }
      } catch (e) {
        if (cancelled) return;
        console.error("[useOwnerCheck] exception", e);
        setIsOwner(false);
      } finally {
        if (!cancelled) setLoading(false);
        if (timeoutId) clearTimeout(timeoutId);
      }
    };
    check();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [user?.id]);

  return { isOwner, loading };
};
